import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Container,
  CssBaseline,
  Stack,
  alpha,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import AppTheme from '../../../shared-theme/AppTheme';
import SideMenu from '../../../components/SideMenu';
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import {
  ArrowBack, Add, EditOutlined, CheckCircleOutline, MailOutline, PhoneOutlined, BadgeOutlined, PlaceOutlined,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import { formatNumber } from '../../../utils/generalFunctions';
import { MetricCard, SectionTitle, EstadoChip, InitialsAvatar, ACCENTS, formatFecha, toInputDate } from './inversionistasUI';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const EMPTY_ACUERDO = {
  capital: '',
  porcentaje_rendimiento: '',
  tipo_rendimiento: 'mensual',
  fecha_inicio: '',
  fecha_fin: '',
  notas: '',
};

const InfoLine = ({ icon: Icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Icon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.25 }} />
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
  </Stack>
);

export default function DetalleInversionista(props) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inversionista, setInversionista] = useState({});
  const [acuerdos, setAcuerdos] = useState([]);
  const [gananciaGeneral, setGananciaGeneral] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_ACUERDO);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const idcompany = localStorage.getItem('idcompany');
  const usuarioId = localStorage.getItem('id');
  const headers = {
    'Content-Type': 'application/json',
    'X-Usuario-ID': usuarioId,
    'X-company-ID': idcompany,
  };

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchInversionista = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/inversionistas/detalle/${id}`,
        { headers }
      );
      if (response.ok) setInversionista(await response.json());
    } catch (error) {
      console.error('Error fetching inversionista:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAcuerdos = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/acuerdos-inversionistas/${id}`,
        { headers }
      );
      if (response.ok) setAcuerdos(await response.json());
    } catch (error) {
      console.error('Error fetching acuerdos:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchGananciaGeneral = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/empresa/ganancia-general/${idcompany}`,
        { headers }
      );
      if (response.ok) setGananciaGeneral(await response.json());
    } catch (error) {
      console.error('Error fetching ganancia general:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idcompany]);

  const refreshAll = useCallback(() => {
    fetchInversionista();
    fetchAcuerdos();
    fetchGananciaGeneral();
  }, [fetchInversionista, fetchAcuerdos, fetchGananciaGeneral]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const handleOpenDialog = (acuerdo = null) => {
    if (acuerdo) {
      setEditingId(acuerdo.id);
      setFormData({
        capital: acuerdo.capital ?? '',
        porcentaje_rendimiento: acuerdo.porcentaje_rendimiento ?? '',
        tipo_rendimiento: acuerdo.tipo_rendimiento || 'mensual',
        fecha_inicio: toInputDate(acuerdo.fecha_inicio),
        fecha_fin: toInputDate(acuerdo.fecha_fin),
        notas: acuerdo.notas || '',
      });
    } else {
      setEditingId(null);
      setFormData(EMPTY_ACUERDO);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!formData.capital || !formData.porcentaje_rendimiento || !formData.fecha_inicio) {
      showSnackbar('Capital, porcentaje y fecha de inicio son obligatorios', 'warning');
      return;
    }
    try {
      const url = editingId
        ? `${process.env.REACT_APP_SERVER_URL}/api/acuerdos-inversionistas/${editingId}`
        : `${process.env.REACT_APP_SERVER_URL}/api/acuerdos-inversionistas`;
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          ...formData,
          id_inversionista: id,
          idcompany,
          capital: parseFloat(formData.capital),
          porcentaje_rendimiento: parseFloat(formData.porcentaje_rendimiento),
          fecha_fin: formData.fecha_fin || null,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        showSnackbar(editingId ? 'Acuerdo actualizado' : 'Acuerdo creado');
        handleCloseDialog();
        refreshAll();
      } else {
        showSnackbar(data.error || 'Error al guardar el acuerdo', 'error');
      }
    } catch (error) {
      console.error('Error saving acuerdo:', error);
      showSnackbar('Error al guardar el acuerdo', 'error');
    }
  };

  const handleFinalizar = async (acuerdoId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/acuerdos-inversionistas/${acuerdoId}/finalizar`,
        { method: 'POST', headers }
      );
      if (response.ok) {
        showSnackbar('Acuerdo finalizado');
        refreshAll();
      } else {
        showSnackbar('No se pudo finalizar el acuerdo', 'error');
      }
    } catch (error) {
      console.error('Error finalizando acuerdo:', error);
      showSnackbar('Error al finalizar el acuerdo', 'error');
    }
  };

  const previewRendimiento =
    formData.capital && formData.porcentaje_rendimiento
      ? (parseFloat(formData.capital) * parseFloat(formData.porcentaje_rendimiento)) / 100
      : 0;

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack spacing={2} sx={{ alignItems: 'center', mx: 3, pb: 5, mt: { xs: 8, md: 0 } }}>
            <Header parent={'Gestión Comercial'} child={'Inversionistas / Detalle'} />
            <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
              <Box maxWidth="lg" mx="auto" display="flex" flexDirection="column" alignItems="flex-start">
                {/* Cabecera */}
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/gestion_comercial/inversionistas')}
                  sx={{ fontWeight: 600, textTransform: 'none', color: 'text.secondary', px: 0.5, mb: 1, justifyContent: 'flex-start' }}
                >
                  Inversionistas
                </Button>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <InitialsAvatar name={inversionista.nombre} size={52} />
                    <Box>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="h4" fontWeight={700} letterSpacing="-0.01em" textAlign="left">
                          {inversionista.nombre || 'Inversionista'}
                        </Typography>
                        {inversionista.estado && <EstadoChip estado={inversionista.estado} />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" textAlign="left">
                        {inversionista.identificacion || 'Sin identificación'}
                      </Typography>
                    </Box>
                  </Stack>
                
                </Box>

                {/* Métricas del inversionista */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
                    <MetricCard label="Acuerdos activos" value={inversionista.acuerdos_activos || 0} {...ACCENTS.teal} />
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
                    <MetricCard label="Capital invertido" value={formatNumber(inversionista.capital_invertido || 0)} {...ACCENTS.green} />
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
                    <MetricCard label="Rendimiento mensual" value={formatNumber(inversionista.rendimiento_mensual || 0)} {...ACCENTS.orange} />
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
                    <MetricCard label="Rendimiento único" value={formatNumber(inversionista.rendimiento_unico || 0)} {...ACCENTS.purple} />
                  </Grid>
                </Grid>

                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  {/* Información del inversionista */}
                  <Grid item size={{ xs: 12, md: 5 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', height: '100%' }}>
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>Información General</SectionTitle>
                        <Stack spacing={2}>
                          <InfoLine icon={BadgeOutlined} label="Identificación" value={inversionista.identificacion} />
                          <InfoLine icon={MailOutline} label="Email" value={inversionista.email} />
                          <InfoLine icon={PhoneOutlined} label="Teléfono" value={inversionista.telefono} />
                          <InfoLine icon={PlaceOutlined} label="Dirección" value={inversionista.direccion} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Ganancia general de la empresa */}
                  <Grid item size={{ xs: 12, md: 7 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', height: '100%' }}>
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>Ganancia General de la Empresa</SectionTitle>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          Período: {gananciaGeneral?.fecha_inicio || '—'} a {gananciaGeneral?.fecha_fin || '—'} · intereses + moras de pagos confirmados
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item size={{ xs: 6 }}>
                            <MetricCard label="Intereses cobrados" value={formatNumber(gananciaGeneral?.intereses_cobrados || 0)} {...ACCENTS.teal} />
                          </Grid>
                          <Grid item size={{ xs: 6 }}>
                            <MetricCard label="Moras cobradas" value={formatNumber(gananciaGeneral?.moras_cobradas || 0)} {...ACCENTS.teal} />
                          </Grid>
                          <Grid item size={{ xs: 6 }}>
                            <MetricCard label="Ganancia general" value={formatNumber(gananciaGeneral?.ganancia_general || 0)} {...ACCENTS.green} />
                          </Grid>
                          <Grid item size={{ xs: 6 }}>
                            <MetricCard label="Rend. comprometido" value={formatNumber(gananciaGeneral?.rendimiento_mensual_comprometido || 0)} {...ACCENTS.orange} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Acuerdos */}
                <SectionTitle
                  action={
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      sx={{ fontWeight: 600, textTransform: 'none' }}
                      onClick={() => handleOpenDialog()}
                    >
                      Nuevo Acuerdo
                    </Button>
                  }
                >
                  Acuerdos de Inversión ({acuerdos.length})
                </SectionTitle>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
                >
                  <Table sx={{ '& td, & th': { borderColor: 'divider' } }}>
                    <TableHead>
                      <TableRow>
                        <StyledCellHeader align="right">Capital</StyledCellHeader>
                        <StyledCellHeader align="center">% Rendimiento</StyledCellHeader>
                        <StyledCellHeader align="center">Tipo</StyledCellHeader>
                        <StyledCellHeader align="right">Rendimiento</StyledCellHeader>
                        <StyledCellHeader align="center">Inicio</StyledCellHeader>
                        <StyledCellHeader align="center">Fin</StyledCellHeader>
                        <StyledCellHeader align="center">Estado</StyledCellHeader>
                        <StyledCellHeader align="center">Acciones</StyledCellHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {acuerdos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                            No hay acuerdos registrados para este inversionista.
                          </TableCell>
                        </TableRow>
                      ) : (
                        acuerdos.map((ac) => (
                          <TableRow key={ac.id} hover>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(ac.capital)}</TableCell>
                            <TableCell align="center">{parseFloat(ac.porcentaje_rendimiento)}%</TableCell>
                            <TableCell align="center">{ac.tipo_rendimiento === 'mensual' ? 'Mensual' : 'Único'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1b5e20' }}>{formatNumber(ac.rendimiento)}</TableCell>
                            <TableCell align="center">{formatFecha(ac.fecha_inicio) || '—'}</TableCell>
                            <TableCell align="center">{formatFecha(ac.fecha_fin) || 'Indefinido'}</TableCell>
                            <TableCell align="center"><EstadoChip estado={ac.estado} /></TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="Editar">
                                  <span>
                                    <IconButton size="small" onClick={() => handleOpenDialog(ac)} disabled={ac.estado !== 'activo'}>
                                      <EditOutlined fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Finalizar">
                                  <span>
                                    <IconButton size="small" color="success" onClick={() => handleFinalizar(ac.id)} disabled={ac.estado !== 'activo'}>
                                      <CheckCircleOutline fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>

      {/* Dialog crear/editar acuerdo */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? 'Editar Acuerdo' : 'Nuevo Acuerdo de Inversión'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Capital aportado</Typography>
              <TextField
                fullWidth
                sx={{ mt: -1 }}
                type="number"
                placeholder="Ej: 100000"
                value={formData.capital}
                onChange={(e) => setFormData({ ...formData, capital: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Rendimiento (%)</Typography>
              <TextField
                fullWidth
                sx={{ mt: -1 }}
                type="number"
                placeholder="Ej: 10"
                inputProps={{ min: 0, step: 0.0001 }}
                value={formData.porcentaje_rendimiento}
                onChange={(e) => setFormData({ ...formData, porcentaje_rendimiento: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Tipo de rendimiento</Typography>
                <Select
                  sx={{ mt: -1, backgroundColor: 'white' }}
                  value={formData.tipo_rendimiento}
                  onChange={(e) => setFormData({ ...formData, tipo_rendimiento: e.target.value })}
                >
                  <MenuItem value="mensual">Mensual</MenuItem>
                  <MenuItem value="unico">Único por el acuerdo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Rendimiento estimado</Typography>
              <TextField fullWidth sx={{ mt: -1 }} value={formatNumber(previewRendimiento)} disabled
                helperText={formData.tipo_rendimiento === 'mensual' ? 'Por mes' : 'Una sola vez'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Fecha inicio</Typography>
              <TextField
                fullWidth
                sx={{ mt: -1 }}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.fecha_inicio}
                onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Fecha fin (opcional)</Typography>
              <TextField
                fullWidth
                sx={{ mt: -1 }}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.fecha_fin}
                onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign="left" fontWeight={600}>Notas</Typography>
              <TextField
                fullWidth
                sx={{ mt: -1 }}
                multiline
                rows={2}
                placeholder="Notas u observaciones del acuerdo"
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primarybutton" sx={{ fontWeight: 600, textTransform: 'none' }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppTheme>
  );
}
